import { useQuery } from '@tanstack/react-query';
import { WorkspacesApi } from '@up2stay/up2promo-api-client';

/**
 * Returns whether the given workspace currently has a team — used to gate
 * features that only make sense in a team context (e.g. the `team_members`
 * profile module, DEV-15).
 *
 * Backed by `GET /workspaces/:id/team-status` (true when the workspace has at
 * least one Team entity).
 */
export function useWorkspaceHasTeam(workspaceId: string | null | undefined): {
  hasTeam: boolean;
  activeMemberCount: number;
  teamCount: number;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ['workspace-team-status', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async () => {
      const res = await WorkspacesApi.getTeamStatus({
        path: { id: workspaceId as string },
      } as never);
      return res.data ?? null;
    },
    // Cheap call but no reason to refetch on every focus.
    staleTime: 30_000,
  });

  return {
    hasTeam: Boolean(workspaceId) && Boolean(data?.hasTeam),
    activeMemberCount: data?.activeMemberCount ?? 0,
    teamCount: data?.teamCount ?? 0,
    isLoading: Boolean(workspaceId) && isLoading,
  };
}
